<script lang="ts">
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import { getProductPath } from "../../../../core/schema/paths/getProductPath.js";
	import macaroonIcon from "../../../../core/assets/icons/macaroon.svg?raw";
	import Link from "../Link.svelte";
	import type { ProductPhoto } from "../../../../core/schema/ProductPhoto.js";
	import { getPriceOfOrderProduct } from "../../../../core/schema/utils/getPriceOfOrderProduct.js";
	import Icon from "../Icon.svelte";
	import trash from "../../../../core/assets/icons/trash.svg?raw";
	import ProductCardImage from "../Product/ProductCardImage.svelte";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { reportAddedProductToCart, reportRemovedProductFromCart } from "../../UniversalAnalytics.js";
	import ProductConfigurator from "../Configurator/ProductConfigurator.svelte";
	import type { OrderProductOptions } from "../../../../core/schema/OrderProductOptions.js";
	import { isProductForCustomerConfigurable } from "../../../../core/schema/utils/isProductForCustomerConfigurable.js";
	import Button from "../Button.svelte";
	import { emptyOptions } from "../../../../core/schema/utils/emptyOptions.js";
	import settings from "../../../../core/assets/icons/settings.svg?raw";
	import NumberInput from "../Form/NumberInput.svelte";
	import { ProductSize } from "../../../../core/schema/ProductSize.js";
	import ConfigPill from "../ConfigPill.svelte";
	import bezlepek from "../../../../core/assets/icons/bezlepek.svg?raw";
	import filling from "../../../../core/assets/icons/filling.svg?raw";
	import bezlaktoza from "../../../../core/assets/icons/bezlaktoza.svg?raw";
	import ovocenavic from "../../../../core/assets/icons/ovocenavic.svg?raw";
	import pusinky from "../../../../core/assets/icons/pusinky.svg?raw";
	import cakeConf from "../../../../core/assets/icons/cakeconf.svg?raw";
	import cislodort from "../../../../core/assets/icons/cislodort.svg?raw";
	import cedulkydort from "../../../../core/assets/icons/cedulkydort.svg?raw";
	import napisdort from "../../../../core/assets/icons/napisdort.svg?raw";
	import "../../../../core/assets/css/cart-product-grid.css";
	import type { OrderProduct } from "../../../../core/schema/OrderProduct.js";
	import {
		MacaroonVariant,
		macaroonVariantKeyMap,
		macaroonVariantNameMap,
	} from "../../../../core/schema/MacaroonVariant.js";
	import { minMacaroons } from "../../../../core/schema/productOptionsConstants.js";
	import { getOrderProductMaximumQuantity } from "../../../../core/schema/utils/getProductMaximumQuantity.js";
	import type { ProductAmountPerCategory } from "../../../../core/schema/ProductAmountPerCategory.js";
	import { getCartStore } from "../../contexts/cartStoreContextKey.js";
	import { productSizeNameMap } from "../../../../core/schema/names/productSizeNameMap.js";
	import { ProductSizeLabelType } from "../../../../core/schema/ProductSizeLabelType.js";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";
	import { productSizeLabelTypeShorthandNameMap } from "../../../../core/schema/names/productSizeLabelTypeShorthandNameMap.js";
	import { formatNumber } from "../../../../core/utils/formatNumber.js";
	import { shouldShowProductForCustomerSize } from "../../../../core/schema/utils/shouldShowProductForCustomerSize.js";
	import { doesOrderProductHaveAnyOption } from "../../../../core/schema/utils/doesOrderProductHaveAnyOption.js";
	import { creamColorNameMap } from "../../../../core/schema/names/creamColorNameMap.js";
	import { productFillingNameMap } from "../../../../core/schema/names/productFillingNameMap.js";
	import { doesProductForCustomerHaveMultipleSizes } from "../../../../core/schema/utils/doesProductForCustomerHaveMultipleSizes.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";

	export let orderProduct: OrderProduct;
	export let maxProductsPerCategory: ProductAmountPerCategory;
	export let index: number;

	const cartStore = getCartStore();
	$: ({ product, quantity, options, size } = orderProduct);
	$: productDetail = $cartStore.value.productsInOrder.find(({ id }) => id === orderProduct.product.id);
	$: minimumOrderQuantity = productDetail?.minimumOrderQuantity ?? 1;
	$: maximumOrderQuantity = getOrderProductMaximumQuantity(
		orderProduct,
		maxProductsPerCategory,
		$cartStore.value.order.products,
		$cartStore.value.productsInOrder,
		$cartStore.value.order.isExpress,
	);

	const { TENANT_LOCALE, WEB_URL } = getGlobalSettings();

	async function onAmountChange({ detail: newQuantity }: CustomEvent<number>): Promise<void> {
		const oldQuantity = quantity;
		if (newQuantity < minimumOrderQuantity) {
			const confirmText = ["Opravdu chcete odebrat tento produkt?"];
			if (minimumOrderQuantity > 1) {
				confirmText.unshift(
					`Tento produkt lze objednat minimálně v ${minimumOrderQuantity} kusech. Touto akcí bude produkt odebrán z krabice.`,
				);
			}

			// eslint-disable-next-line no-alert
			const confirmed = confirm(confirmText.join(" "));
			if (confirmed) {
				await cartStore.removeProduct(index);
				reportRemovedProductFromCart(orderProduct, TENANT_LOCALE);
				return;
			}
			const activeElement = document.activeElement as HTMLElement | undefined;
			activeElement?.blur(); // Remove focus from the NumberInput so we don't cause an infinite loop if it is being held.
			// Force update the quantity. The number input gets stuck on the new (invalid) value if we don't do this.
			quantity = newQuantity;
			quantity = oldQuantity;
			await cartStore.setProductQuantity(index, oldQuantity);
			return;
		}
		await cartStore.setProductQuantity(index, newQuantity);
		if (newQuantity > oldQuantity) {
			reportAddedProductToCart(orderProduct, TENANT_LOCALE, newQuantity - oldQuantity);
		} else {
			reportRemovedProductFromCart(orderProduct, TENANT_LOCALE, oldQuantity - newQuantity);
		}
	}

	async function onRemove(): Promise<void> {
		await cartStore.removeProduct(index);
		reportRemovedProductFromCart(orderProduct, TENANT_LOCALE);
	}

	let configurationDrawerOpen = false;
	function openConfigurator(): void {
		configurationDrawerOpen = true;
	}

	async function saveConfiguratorOptions(event: CustomEvent<OrderProductOptions>): Promise<void> {
		await cartStore.updateItemOptions(index, event.detail);
		configurationDrawerOpen = false;
	}

	$: allProductPrice = getPriceOfOrderProduct(orderProduct);
	$: singleProductPrice = getPriceOfOrderProduct({ ...orderProduct, quantity: 1 });
	$: photos = [options?.lyingPhoto, ...(options?.standingPhotos ?? [])].filter((photo): photo is ProductPhoto =>
		Boolean(photo),
	);

	$: productHasOptions = doesOrderProductHaveAnyOption(orderProduct.options);

	const sizeClassMap: Record<ProductSize, string> = {
		[ProductSize.Xs]: "h-[1.35rem]",
		[ProductSize.S]: "h-[1.575rem]",
		[ProductSize.M]: "h-[1.75rem]",
		[ProductSize.L]: "h-8",
	};

	function withExpressQueryParam(link: string): string {
		const url = new URL(`${WEB_URL}${link}`);
		url.searchParams.set("express", "");
		return `${url.pathname}${url.search}`;
	}

	function withSizeQueryParam(link: string, size: ProductSize): string {
		const url = new URL(`${WEB_URL}${link}`);
		url.searchParams.set("size", size);
		return `${url.pathname}${url.search}`;
	}

	function getLinkToProduct(link: string, product: ProductForCustomer | undefined, selectedSize: ProductSize): string {
		if (product && doesProductForCustomerHaveMultipleSizes(product)) {
			link = withSizeQueryParam(link, selectedSize);
		}

		if ($cartStore.value.order.isExpress) {
			link = withExpressQueryParam(link);
		}

		return link;
	}

	$: link = `/${getProductPath(TENANT_LOCALE, product)}`;
	$: linkToProduct = getLinkToProduct(link, productDetail, size);
</script>

{#if productDetail && isProductForCustomerConfigurable(productDetail, false)}
	<ProductConfigurator
		allowExpress={$cartStore.value.order.isExpress}
		isExpress={$cartStore.value.order.isExpress}
		bind:isOpen={configurationDrawerOpen}
		product={productDetail}
		{size}
		options={orderProduct.options ?? emptyOptions}
		on:saveOptions={saveConfiguratorOptions}
	/>
{/if}

<div class="border-b-1 border-secondary-200 cart-product-grid py-3 xl:py-6">
	<a href={linkToProduct} class="grid-image group relative h-[3.5rem] w-full sm:h-[5rem] lg:h-[6.5rem] xl:h-[9rem]">
		{#if productDetail}
			<ProductCardImage
				loading={index < 7 ? "eager" : "lazy"}
				product={productDetail}
				selectedSize={size}
				size={144}
				logo={{
					class: "w-8 sm:w-12 md:w-14",
					size: 56,
				}}
			/>
		{/if}
	</a>
	<Link
		class="grid-name hover:!decoration-secondary md:text-md mt-1 line-clamp-2 text-base font-semibold leading-[1.25] decoration-transparent xl:text-lg"
		href={linkToProduct}
	>
		{product.name[TENANT_LOCALE]}
	</Link>
	<div
		class="grid-calculation grid grid-cols-[5rem,6rem,auto] items-center gap-2 text-right md:h-fit md:grid-cols-[1fr,6rem,auto] lg:grid-cols-[1fr,7.5rem,auto] lg:gap-4 xl:grid-cols-[1fr,7.5rem,12rem] xl:gap-6"
	>
		<span class="text-3xs min-[380px]:text-2xs tracking-tighter lg:text-xs">
			{nbspify(formatPrice(singleProductPrice.withTax, TENANT_LOCALE, true))}&nbsp;/&nbsp;ks
		</span>
		<div class="flex w-full gap-2">
			<NumberInput
				id="product.{index}.quantity"
				class="!h-8 w-[6rem] !gap-1 !p-1 lg:!h-10 lg:w-[7.5rem] lg:!p-2"
				min={minimumOrderQuantity - 1}
				max={maximumOrderQuantity}
				required
				value={quantity}
				on:change={onAmountChange}
			/>
		</div>
		<span class="min-[380px]:text-md font-bold tracking-tighter xl:text-lg">
			{nbspify(formatPrice(allProductPrice.withTax, TENANT_LOCALE, true))}
		</span>
	</div>
	<button
		class="grid-trash mt-1 flex h-fit w-full items-center justify-center sm:mt-2 lg:mt-3"
		type="button"
		on:click={onRemove}
	>
		<Icon
			icon={trash}
			class="hover:text-secondary-300 text-secondary-250 aspect-square h-4 cursor-pointer transition-colors duration-300"
		/>
	</button>
	{#if productDetail !== undefined && shouldShowProductForCustomerSize(productDetail, orderProduct)}
		{@const customSizeLabel =
			orderProduct.product.sizeLabel && orderProduct.product.sizeLabel.type !== ProductSizeLabelType.Serving}

		<div class="grid-size flex items-center gap-2 md:gap-4">
			<span class="!text-3xs md:!text-2xs">Velikost</span>
			<div
				class="text-2xs border-secondary-200 flex items-center justify-center rounded-full border font-medium {sizeClassMap[
					size
				]}"
				class:px-2={customSizeLabel}
				class:aspect-square={!customSizeLabel}
			>
				{#if orderProduct.product.sizeLabel && orderProduct.product.sizeLabel.type !== ProductSizeLabelType.Serving}
					<div class="flex gap-0.5">
						<span>
							{formatNumber(orderProduct.product.sizeLabel.values[productSizeKeyMap[size]], TENANT_LOCALE, false)}
						</span>
						<span>{productSizeLabelTypeShorthandNameMap[orderProduct.product.sizeLabel.type]}</span>
					</div>
				{:else}
					{productSizeNameMap[size][TENANT_LOCALE]}
				{/if}
			</div>
		</div>
	{/if}
	{#if options}
		<div class="grid-configuration grid gap-2 {productHasOptions ? 'mb-2 md:mb-0' : ''}">
			<div class="flex items-start gap-2 md:gap-4">
				{#if productHasOptions}
					<span class="!text-3xs md:!text-2xs mt-1">Úpravy</span>
				{/if}
				<div class="flex flex-wrap gap-1 md:gap-x-[.35rem]">
					{#if options.numbers.length > 0}
						{#each options.numbers as { number }}
							<ConfigPill name="Číslice" icon={cislodort} value={number} />
						{/each}
					{/if}
					{#if options.extraFruit}
						<ConfigPill name="Ovoce navíc" icon={ovocenavic} />
					{/if}
					{#if options.glutenFree}
						<ConfigPill name="Bez lepku" icon={bezlepek} />
					{/if}
					{#if options.lactoseFree}
						<ConfigPill name="Bez laktózy" icon={bezlaktoza} />
					{/if}
					{#if options.meringues}
						<ConfigPill name="Sněhové pusinky" icon={pusinky} />
					{/if}
					{#if options.creamColor}
						<ConfigPill
							name="Barevná šlehačka"
							icon={cakeConf}
							value={creamColorNameMap[options.creamColor][TENANT_LOCALE]}
						/>
					{/if}
					{#if options.macaroons}
						{#each Object.values(MacaroonVariant) as variant}
							{@const key = macaroonVariantKeyMap[variant]}
							{@const amount = options.macaroons[key]}
							{#if amount >= minMacaroons}
								<ConfigPill
									name="Makronka"
									icon={macaroonIcon}
									value="{macaroonVariantNameMap[variant][TENANT_LOCALE]} ({amount}ks)"
								/>
							{/if}
						{/each}
					{/if}
					{#if options.labels.length > 0}
						{#each options.labels as label, index}
							<ConfigPill name="Cedulka" icon={cedulkydort} {index} value={label.text} />
						{/each}
					{/if}
					{#if options.inscription}
						<ConfigPill name="Nápis" icon={napisdort} value={options.inscription.text} />
					{/if}
					{#if options.filling}
						<ConfigPill name="Náplň" icon={filling} value={productFillingNameMap[options.filling][TENANT_LOCALE]} />
					{/if}
				</div>
			</div>
			{#if photos.length > 0}
				<div class="flex items-start gap-2 md:gap-4">
					<span class="!text-3xs md:!text-2xs mt-2">Obrázky</span>
					<div class="flex flex-wrap gap-2">
						{#each photos as photo}
							{@const defaultAlt = `Obrázek produktu ${product.name[TENANT_LOCALE]}`}
							{@const size = 64}
							{@const lightboxId = product.id}
							<DynamicPicture
								imgClass="rounded-md"
								image={photo.file}
								width={size}
								height={size}
								alt={photo.file.title ?? defaultAlt}
								{lightboxId}
								lightboxWidth={600}
								bucket="upload"
							/>
						{/each}
					</div>
				</div>
			{/if}
		</div>
	{/if}
	{#if productDetail && isProductForCustomerConfigurable(productDetail, false)}
		<Button
			class="grid-button flex h-fit rounded-md !p-[.4rem] py-1 text-xs sm:rounded-full sm:!py-1 md:self-end md:justify-self-end lg:!px-[1.125rem] lg:py-[.4375rem] lg:text-base"
			type="button"
			variant="secondary"
			on:click={openConfigurator}
			icon={settings}
			iconClass="h-[.65rem] sm:h-3 lg:h-4"
			iconFront
		>
			<span class="hidden sm:inline">
				{#if productHasOptions}
					Změnit úpravy
				{:else}
					Přidat úpravy
				{/if}
			</span>
		</Button>
	{/if}
</div>
